const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, "");

export type NormalizeAssetPathOptions = {
  fallback?: string;
  stripTrailingSlash?: boolean;
  stripQueryHash?: boolean;
  removeDotSegments?: boolean;
  lowercase?: boolean;
};

export const normalizeAssetPath = (
  value: string | null | undefined,
  {
    fallback = "",
    stripTrailingSlash = false,
    stripQueryHash = false,
    removeDotSegments = false,
    lowercase = false,
  }: NormalizeAssetPathOptions = {}
): string => {
  let normalized = String(value || fallback || "")
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");
  if (stripQueryHash) {
    normalized = normalized.split("?")[0]?.split("#")[0] ?? normalized;
  }
  if (removeDotSegments) {
    normalized = normalized
      .split("/")
      .filter(Boolean)
      .filter((part) => part !== ".")
      .join("/");
  }
  if (stripTrailingSlash) normalized = normalized.replace(/\/+$/, "");
  return lowercase ? normalized.toLowerCase() : normalized;
};

const readEnvString = (value: unknown): string => {
  if (typeof value !== "string") return "";
  return value.trim();
};

export const getLargeAssetBaseUrl = (): string => {
  const raw = readEnvString(import.meta.env?.VITE_LARGE_ASSET_BASE_URL);
  if (!raw) return "";
  return trimTrailingSlash(raw);
};

export const getCritterAssetsBaseUrl = (): string => {
  if (import.meta.env?.DEV && typeof window !== "undefined") {
    // DEV-only override for local or shared asset-host testing. Do not enable in production.
    const override = readEnvString(
      new window.URLSearchParams(window.location.search).get("critterAssetsBaseUrl")
    );
    if (override) return trimTrailingSlash(override);
  }
  const raw = readEnvString(import.meta.env?.VITE_CRITTER_ASSETS_BASE_URL);
  return trimTrailingSlash(
    raw || "https://raw.githubusercontent.com/CritterEngine/critter-assets/main"
  );
};

const isAbsoluteUrl = (value: string): boolean => /^https?:\/\//i.test(value);

const normalizePublicPath = (path: string): string => {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
};

const isLargeAssetPath = (path: string): boolean =>
  path.startsWith("/s1/") || path.startsWith("/objects/thor/");

export const resolveAssetUrl = (path: string): string => {
  if (!path) return path;
  if (isAbsoluteUrl(path)) return path;
  const normalizedPath = normalizePublicPath(path);
  const base = getLargeAssetBaseUrl();
  if (!base) return normalizedPath;
  if (!isLargeAssetPath(normalizedPath)) return normalizedPath;
  return `${base}${normalizedPath}`;
};

export const resolveCritterAssetsUrl = (path: string): string => {
  if (!path) return path;
  if (isAbsoluteUrl(path)) return path;
  return `${getCritterAssetsBaseUrl()}${normalizePublicPath(path)}`;
};
